import type { DeliveryPlatform, Ingredient, Investment, InventoryCount, MenuItem, Recipe, Supplier, Target, TenantSettings } from './types';
import type { RecipeProposal } from './recipeProposal';
import { buildContext, computeRecipeCost } from './costing';
import { valueInventoryLines } from './inventory';
import { demoDataset, DEMO_MONTHS } from './seed';

/**
 * Demo de la web (demo.alexiarest.com): una taquería de la Ciudad de México,
 * «Taquería Doña Lupe» (rediseño, 11 oct 2026). Alex: «nadie se cree un
 * ticket de 22 pesos ni un solomillo con pesto en una cuenta en pesos». Los
 * precios son de 2026 y en pesos de verdad, no euros por un factor.
 *
 * Ventas, gastos y objetivos salen de los mismos generadores que
 * `demoDataset` (que sigue siendo el fixture de los tests); aquí cambian la
 * carta, los ingredientes, las recetas, los proveedores y los tickets.
 */

const T = 'demo';
const now = new Date().toISOString();
const IVA = 16;
const haceCuatroMeses = new Date(Date.now() - 120 * 86_400_000).toISOString();

export const demoMxTenantName = 'Taquería Doña Lupe';

export const demoMxPlatforms: DeliveryPlatform[] = [
  { platformId: 'plt-uber', name: 'Uber Eats', commissionPct: 30, active: true },
  { platformId: 'plt-rappi', name: 'Rappi', commissionPct: 25, active: true },
  { platformId: 'plt-didi', name: 'DiDi Food', commissionPct: 22, active: false },
];

export const demoMxSettings: TenantSettings = {
  vatPct: IVA,
  bankCommissionPct: 3.5,
  platforms: demoMxPlatforms,
  expenseSubgroups: {
    materia_prima: ['Carnes y mariscos', 'Bebidas', 'Frutas y verduras'],
    personal: ['Cocina', 'Salón'],
    generales: ['Local', 'Marketing'],
  },
  alerts: { costIncreasePct: 5, costDecreasePct: 10, minGrossMarginPct: 65 },
};

export const demoMxSuppliers: Supplier[] = [
  // Fase 7: contacto, días de pedido y entrega. Sin recordatorio a propósito:
  // «Para hoy» de la demo no debe cambiar según el día de la semana.
  { tenantId: T, supplierId: 'sup-carnes', name: 'Carnes Roma', contactName: 'Roberto Salas', phone: '668 412 2070', orderDays: [4], orderCutoff: '18:00', deliveryDays: [5], notes: 'Pedir el pollo entero sin despiece, sale más barato.' },
  { tenantId: T, supplierId: 'sup-mariscos', name: 'Mariscos del Pacífico' },
  { tenantId: T, supplierId: 'sup-abasto', name: 'Central de Abasto Don Chuy', contactName: 'Don Chuy', orderDays: [1, 4], deliveryDays: [2, 5] },
  { tenantId: T, supplierId: 'sup-lacteos', name: 'Lácteos del Valle' },
  { tenantId: T, supplierId: 'sup-general', name: 'Distribuciones del Norte' },
  { tenantId: T, supplierId: 'sup-desechables', name: 'Biopack' },
  // Repetido de Carnes Roma (se creó solo con una factura): enseña «¿Son el mismo?».
  { tenantId: T, supplierId: 'sup-carnes-sa', name: 'CARNES ROMA SA DE CV', phone: '+52 668 412 2070' },
];

const ing = (
  ingredientId: string, name: string, brand: string, presentation: string,
  purchaseQty: number, purchaseUnit: Ingredient['purchaseUnit'], netPrice: number,
  group: Ingredient['group'], supplierId: string, extra: Partial<Ingredient> = {},
): Ingredient => ({
  tenantId: T, ingredientId, name, brand, presentation, group, supplierId,
  purchaseQty, purchaseUnit, netPrice, vatPct: IVA, active: true, updatedAt: now, ...extra,
});

/** Precios sin IVA, en pesos. Sal, jitomate y cebolla: «Por confirmar». */
export const demoMxIngredients: Ingredient[] = [
  ing('mx-pastor', 'Pierna de cerdo para pastor', 'Carnes Roma', 'pieza', 5, 'kg', 575, 'carnes_pescados', 'sup-carnes'),
  ing('mx-pollo', 'Pechuga de pollo', 'Carnes Roma', 'paquete', 2, 'kg', 170, 'carnes_pescados', 'sup-carnes'),
  ing('mx-camaron', 'Camarón mediano limpio', 'Mariscos del Pacífico', 'bolsa', 1, 'kg', 290, 'carnes_pescados', 'sup-mariscos', { allergens: ['crustaceos'] }),
  ing('mx-tortilla', 'Tortilla de maíz', 'Tortillería La Esperanza', 'paquete', 1, 'kg', 26, 'secos_granos', 'sup-general'),
  ing('mx-frijol', 'Frijol negro', 'Granos Selectos', 'bolsa', 1, 'kg', 38, 'secos_granos', 'sup-general'),
  ing('mx-aguacate', 'Aguacate Hass', 'Granel', 'caja', 10, 'kg', 650, 'frutas_verduras', 'sup-abasto'),
  ing('mx-jitomate', 'Jitomate saladet', 'Granel', 'caja', 10, 'kg', 220, 'frutas_verduras', 'sup-abasto', { priceUpdatedAt: haceCuatroMeses }),
  ing('mx-tomatillo', 'Tomate verde', 'Granel', 'caja', 10, 'kg', 180, 'frutas_verduras', 'sup-abasto'),
  ing('mx-cebolla', 'Cebolla blanca', 'Granel', 'costal', 20, 'kg', 360, 'frutas_verduras', 'sup-abasto', { priceUpdatedAt: haceCuatroMeses }),
  ing('mx-serrano', 'Chile serrano', 'Granel', 'bolsa', 1, 'kg', 45, 'frutas_verduras', 'sup-abasto'),
  ing('mx-cilantro', 'Cilantro', 'Granel', 'manojo', 0.2, 'kg', 7, 'frutas_verduras', 'sup-abasto'),
  ing('mx-limon', 'Limón sin semilla', 'Granel', 'costal', 20, 'kg', 420, 'frutas_verduras', 'sup-abasto'),
  ing('mx-pina', 'Piña miel', 'Granel', 'pieza', 2, 'kg', 50, 'frutas_verduras', 'sup-abasto'),
  ing('mx-queso', 'Queso Oaxaca', 'Lácteos del Valle', 'pieza', 1, 'kg', 175, 'lacteos_huevos', 'sup-lacteos', { allergens: ['lacteos'] }),
  ing('mx-crema', 'Crema ácida', 'Lácteos del Valle', 'bote', 1, 'l', 75, 'lacteos_huevos', 'sup-lacteos', { allergens: ['lacteos'], densityGPerMl: 1.0 }),
  ing('mx-aceite', 'Aceite vegetal', 'Verde Oro', 'garrafón', 5, 'l', 210, 'aceites', 'sup-general', { densityGPerMl: 0.92 }),
  ing('mx-sal', 'Sal de mar', 'La Fina', 'bolsa', 1, 'kg', 14, 'abarrotes', 'sup-general', { priceUpdatedAt: haceCuatroMeses }),
  ing('mx-achiote', 'Pasta de achiote', 'Sabores del Sureste', 'bolsa', 1, 'kg', 95, 'abarrotes', 'sup-general'),
  ing('mx-guajillo', 'Chile guajillo seco', 'Granel', 'bolsa', 1, 'kg', 180, 'abarrotes', 'sup-abasto'),
  ing('mx-contenedor', 'Contenedor para llevar', 'Biopack', 'caja', 100, 'pz', 320, 'desechables', 'sup-desechables'),
  ing('mx-bolsa', 'Bolsa de papel', 'Biopack', 'paquete', 250, 'pz', 260, 'desechables', 'sup-desechables'),
];

type Linea = [kind: 'i' | 'r', id: string, qty: number, unit: Recipe['components'][number]['unit'], waste?: number, block?: 'empaque'];
const receta = (recipeId: string, name: string, kind: Recipe['kind'], code: string, group: string, lineas: Linea[], extra: Partial<Recipe>): Recipe => ({
  tenantId: T, recipeId, name, kind, code, group,
  components: lineas.map(([k, id, qty, unit, waste = 0, block = undefined]) => ({
    ref: k === 'i' ? { kind: 'ingredient' as const, ingredientId: id } : { kind: 'recipe' as const, recipeId: id },
    block: block ?? 'ingrediente', qty, unit, wastePct: waste,
  })),
  yieldQty: 1, yieldUnit: 'pz', portions: 1, safetyMarginPct: 10, useYieldCostAsBase: false,
  active: true, updatedAt: now, ...extra,
});

export const demoMxRecipes: Recipe[] = [
  receta('mx-salsa-verde', 'Salsa verde', 'subreceta', 'PRE001', 'Preparaciones', [
    ['i', 'mx-tomatillo', 1.5, 'kg', 5], ['i', 'mx-serrano', 80, 'g', 5], ['i', 'mx-cebolla', 200, 'g', 10],
    ['i', 'mx-cilantro', 60, 'g', 20], ['i', 'mx-sal', 20, 'g'],
  ], { yieldQty: 2, yieldUnit: 'kg', useYieldCostAsBase: true }),
  receta('mx-adobo', 'Adobo al pastor', 'subreceta', 'PRE002', 'Preparaciones', [
    ['i', 'mx-guajillo', 150, 'g'], ['i', 'mx-achiote', 200, 'g'], ['i', 'mx-pina', 500, 'g', 40],
    ['i', 'mx-cebolla', 300, 'g', 10], ['i', 'mx-sal', 30, 'g'], ['i', 'mx-aceite', 100, 'ml'],
  ], { yieldQty: 1.5, yieldUnit: 'kg', useYieldCostAsBase: true }),
  receta('mx-frijoles', 'Frijoles refritos', 'subreceta', 'PRE003', 'Preparaciones', [
    ['i', 'mx-frijol', 1.2, 'kg'], ['i', 'mx-cebolla', 200, 'g', 10], ['i', 'mx-aceite', 150, 'ml'], ['i', 'mx-sal', 25, 'g'],
  ], { yieldQty: 3, yieldUnit: 'kg', useYieldCostAsBase: true }),
  receta('mx-tacos-pastor', 'Tacos al pastor (orden de 5)', 'plato', 'TAC001', 'Tacos', [
    ['i', 'mx-pastor', 150, 'g', 10], ['r', 'mx-adobo', 60, 'g'], ['i', 'mx-tortilla', 150, 'g'],
    ['i', 'mx-pina', 30, 'g', 40], ['i', 'mx-cebolla', 20, 'g', 10], ['i', 'mx-cilantro', 5, 'g', 20],
    ['r', 'mx-salsa-verde', 40, 'g'], ['i', 'mx-limon', 30, 'g'],
    ['i', 'mx-contenedor', 1, 'pz', 0, 'empaque'], ['i', 'mx-bolsa', 1, 'pz', 0, 'empaque'],
  ], { steps: 'Marinar la carne en el adobo toda la noche.\nAsar en el trompo y rebanar fino.\nCalentar las tortillas en la plancha.\nServir con piña, cebolla, cilantro y salsa verde.' }),
  receta('mx-tacos-camaron', 'Tacos de camarón (orden de 3)', 'plato', 'TAC002', 'Tacos', [
    ['i', 'mx-camaron', 120, 'g'], ['i', 'mx-tortilla', 90, 'g'], ['i', 'mx-crema', 30, 'ml'],
    ['i', 'mx-aguacate', 40, 'g', 30], ['r', 'mx-salsa-verde', 30, 'g'], ['i', 'mx-aceite', 15, 'ml'],
    ['i', 'mx-contenedor', 1, 'pz', 0, 'empaque'],
  ], { allergens: ['crustaceos', 'lacteos'] }),
  receta('mx-guacamole', 'Guacamole con totopos', 'plato', 'ENT001', 'Entradas', [
    ['i', 'mx-aguacate', 250, 'g', 30], ['i', 'mx-jitomate', 60, 'g', 5], ['i', 'mx-cebolla', 30, 'g', 10],
    ['i', 'mx-serrano', 8, 'g'], ['i', 'mx-cilantro', 8, 'g', 20], ['i', 'mx-limon', 20, 'g'],
    ['i', 'mx-sal', 3, 'g'], ['i', 'mx-tortilla', 100, 'g'], ['i', 'mx-aceite', 30, 'ml'],
  ], {}),
  receta('mx-enchiladas', 'Enchiladas verdes de pollo', 'plato', 'PF001', 'Platos fuertes', [
    ['i', 'mx-pollo', 150, 'g'], ['i', 'mx-tortilla', 120, 'g'], ['r', 'mx-salsa-verde', 150, 'g'],
    ['i', 'mx-crema', 40, 'ml'], ['i', 'mx-queso', 50, 'g'], ['i', 'mx-cebolla', 15, 'g', 10],
    ['r', 'mx-frijoles', 100, 'g'], ['i', 'mx-aceite', 15, 'ml'],
    ['i', 'mx-contenedor', 1, 'pz', 0, 'empaque'],
  ], { allergens: ['lacteos'] }),
];

/** Costo de hoy de cada plato, para fijar el de referencia de la carta. */
function costoDe(recipeId: string): number {
  return computeRecipeCost(recipeId, buildContext(demoMxIngredients, demoMxRecipes)).costPerPortion;
}
const r2 = (n: number) => Math.round(n * 100) / 100;

const plato = (menuItemId: string, recipeId: string, name: string, precio: number, unitsSold: number, extra: Partial<MenuItem> = {}): MenuItem => ({
  tenantId: T, menuItemId, recipeId, name, sellingPriceGross: precio, vatPct: IVA, targetFoodCostPct: 30,
  theoreticalCost: r2(costoDe(recipeId)), unitsSold, active: true, updatedAt: now, ...extra,
});

export const demoMxMenuItems: MenuItem[] = [
  plato('menu-pastor', 'mx-tacos-pastor', 'Tacos al pastor (orden de 5)', 125, 980, { channelPrices: { 'plt-uber': 149, 'plt-rappi': 145 } }),
  // Al camarón le subió el costo desde que se puso el precio: la demo enseña el aviso.
  plato('menu-camaron', 'mx-tacos-camaron', 'Tacos de camarón (orden de 3)', 165, 830, { theoreticalCost: r2(costoDe('mx-tacos-camaron') * 0.86), channelPrices: { 'plt-uber': 189, 'plt-rappi': 185 } }),
  plato('menu-guacamole', 'mx-guacamole', 'Guacamole con totopos', 135, 410, { channelPrices: { 'plt-uber': null, 'plt-rappi': null } }),
  // Sin precio en Uber Eats: la demo enseña «Sin precio en Uber Eats · Ponlo» (nunca se asume el del local).
  plato('menu-enchiladas', 'mx-enchiladas', 'Enchiladas verdes de pollo', 175, 530, { channelPrices: { 'plt-rappi': 199 } }),
];

/** Ticket de una taquería con salón: unos 320 pesos por persona y 780 por mesa. */
const TICKET = 320;
const POR_MESA = 780;
const POR_PEDIDO = 280;

const demoMxRevenues = demoDataset.revenues.map((r) => {
  const enLocal = r.inHouse.efectivo + r.inHouse.tarjeta + r.inHouse.otros;
  const apps = Object.values(r.delivery).reduce((s, v) => s + (v ?? 0), 0);
  return {
    ...r,
    delivery: { 'plt-uber': r2(apps * 0.62), 'plt-rappi': r2(apps * 0.38) },
    customers: Math.max(1, Math.round(enLocal / TICKET)),
    tables: Math.max(1, Math.round(enLocal / POR_MESA)),
    deliveryOrders: Math.max(1, Math.round(apps / POR_PEDIDO)),
  };
});

/** Los subgrupos de la taquería (ver `demoMxSettings`). */
const SUBGRUPO_MX: Record<string, string> = { 'Carnes y pescados': 'Carnes y mariscos', Bodega: 'Bebidas', Sala: 'Salón' };

const demoMxExpenses = demoDataset.expenses.map((e) => {
  const conSubgrupo = e.subgroup && SUBGRUPO_MX[e.subgroup] ? { ...e, subgroup: SUBGRUPO_MX[e.subgroup] } : e;
  return e.entryId === 'exp-pendiente-2' ? { ...conSubgrupo, concept: 'Cervecería del Centro — cerveza', invoiceNumber: 'CC-8891' } : conSubgrupo;
});

const demoMxInvestments: Investment[] = demoDataset.investments.map((inv) => ({
  ...inv,
  concept: inv.investmentId === 'inv-horno' ? 'Trompo para pastor y plancha' : inv.investmentId === 'inv-tpv' ? 'Punto de venta y software' : inv.concept,
}));

const demoMxInventoryCounts: InventoryCount[] = demoDataset.inventoryCounts.map((count, i) => {
  const { lines, totalValue } = valueInventoryLines([
    { ingredientId: 'mx-pastor', qty: i === 0 ? 18 : 14 },
    { ingredientId: 'mx-camaron', qty: i === 0 ? 6 : 8 },
    { ingredientId: 'mx-aceite', qty: i === 0 ? 20 : 15 },
    { ingredientId: 'mx-tortilla', qty: i === 0 ? 25 : 30 },
    { ingredientId: 'mx-aguacate', qty: i === 0 ? 12 : 9 },
    { ingredientId: 'mx-queso', qty: i === 0 ? 5 : 4 },
    { ingredientId: 'mx-contenedor', qty: i === 0 ? 320 : 260 },
  ], demoMxIngredients);
  return { ...count, lines, totalValue };
});

const demoMxTargets: Target[] = demoDataset.targets.map((t) => ({ ...t, averageTicket: 330 }));

/**
 * El borrador que enseña «Nuevo plato» en la demo: un pozole con tres
 * ingredientes que la taquería aún no tiene (se crean «por confirmar»).
 */
export const DEMO_MX_RECIPE_PROPOSAL: RecipeProposal = {
  name: 'Pozole rojo', servings: 4, lines: [
    { rawText: '600 gramos de maíz pozolero', ingredientName: 'Maíz pozolero', qty: 600, unit: 'g', estimated: {} },
    { rawText: 'medio kilo de maciza de cerdo', ingredientName: 'Maciza de cerdo', qty: 500, unit: 'g', estimated: {} },
    { rawText: '100 gramos de rábano', ingredientName: 'Rábano', qty: 100, unit: 'g', estimated: {} },
  ],
};

export const demoDatasetMx = {
  ...demoDataset,
  settings: demoMxSettings,
  platforms: demoMxPlatforms,
  suppliers: demoMxSuppliers,
  ingredients: demoMxIngredients,
  recipes: demoMxRecipes,
  menuItems: demoMxMenuItems,
  revenues: demoMxRevenues,
  expenses: demoMxExpenses,
  investments: demoMxInvestments,
  inventoryCounts: demoMxInventoryCounts,
  targets: demoMxTargets,
  months: DEMO_MONTHS,
};
